import { DecimalPipe, NgStyle } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChatbotConfig, GptChatModelTypeArr, isReasoningModel } from '@shared/index';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { TextareaModule } from 'primeng/textarea';
import { SubmenuserviceService } from 'src/app/shared/components/submenu/submenuservice.service';
import { GotoBlocklyDirective } from 'src/app/shared/directives/goto-blockly.directive';
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';
import { LoginService } from '@shared-angular/services/login.service';
import { getContrastColorSafe } from '@shared-angular';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { StageAdminService } from '@shared-angular/services/stage-admin.service';
import { CodemirrorCssComponent } from "../../../../../../shared/components/ai-bot/utils/codemirror-css/codemirror-css.component";
import { FormForBooleanComponent } from '../../../../../../shared/components/ai-bot/utils/form-for-boolean/form-for-boolean.component';
import { ManualLinkComponent } from "../../../../../../shared/components/rag-admin/manual-link/manual-link.component";
import { SubmenuComponent } from '../../../../../../shared/components/submenu/submenu.component';
import { VLLMChatModelTypeArr, reasoningEffortOptions } from './../../../../../../../../../shared/src/ChatbotConfig';
import { HintComponent } from '../../../../../../../../../shared/angular/components/hint/hint.component';
import { BotImageReloadComponent } from '../../../../../../shared/components/bot-image-reload/bot-image-reload.component';

export const TRANSPARENT_COLOR = 'transparent';

export const capitalizeOrParse = (str: string) => {
  str = str.replace('iframely_', '');
  if (str === 'youtube') {
    return 'YouTube';
  }
  if (str === 'htmlpage') {
    return $localize`:@@fusion-htmlpage:HTML`;
  }
  if (str === 'none') {
    return $localize`:@@fusion-none:Keiner`;
  }
  return str.substring(0, 1).toUpperCase() + str.substring(1);
};

@Component({
  templateUrl: './configurations-tab.component.html',
  styleUrls: ['./configurations-tab.component.scss'],
  imports: [NgStyle, DecimalPipe,
    FormsModule,
    CodemirrorCssComponent,
    HintComponent,
    ManualLinkComponent,
    SubmenuComponent,
    GotoBlocklyDirective,
    CardModule,
    SelectModule,
    SliderModule,
    MessageModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    FormForBooleanComponent,
    BotImageReloadComponent
  ]
})
export class ConfigurationsTabComponent {
  public iconList: string[] = [
    'ri-brain-line',
    'ri-robot-line',
    'ri-robot-2-line',
    'ri-live-line',
    'ri-mic-line',
    'ri-home-line',
    'ri-building-line',
    'ri-global-line',
    'ri-shake-hands-line',
    'ri-chat-1-line',
    'ri-database-2-line',
    'ri-file-line',
    'ri-user-line'
  ];

  public colorList: string[] = [
    '#a5bfbe',
    '#f1e5cd',
    '#ecc290',
    '#d4beb1',
    '#bec1c6',
    TRANSPARENT_COLOR
  ];

  public gptModels = GptChatModelTypeArr;
  public vllmModels = VLLMChatModelTypeArr;
  public modelSources: (ChatbotConfig['modelSource'])[] = ['openai', 'vllm'];
  public readonly isReasoningModel = isReasoningModel;

  public textVerbosityOptions = [
    { name: '-', value: null },
    { name: 'Low', value: 'low' },
    { name: 'Medium', value: 'medium' },
    { name: 'High', value: 'high' }
  ];

  public customCssOpen: boolean = false;
  public customJsOpen: boolean = false;
  public wordReplacementOpen: boolean = false;
  public loadingMessagesOpen: boolean = false;
  private openImageConfigRevealed: boolean = false;
  public rows: number = 5;
  public enterCustomCss = $localize`:@@enter-custom-css:Gib deinen CSS-Code hier ein…`;
  public clearChatDescription = $localize`:@@clear-chat-description:Chatbot immer von Anfang starten`;
  public isTemplateLabel = $localize`:@@is-template:Template`;
  public capitalizeOrParse = capitalizeOrParse;
  public getContrastColor = getContrastColorSafe;
  protected jsDetectedErrors: boolean = false;

  public speechRecognitionOptions = [
    {
      label: $localize`:@@speech-recognition-locale:Botsprache`,
      value: 'locale'
    },
    {
      label: $localize`:@@speech-recognition-auto:Automatische Erkennung`,
      value: 'auto'
    }
  ];

  constructor(
    protected rss: RouteShareService,
    protected stageAdmin: StageAdminService,
    public submenuService: SubmenuserviceService,
    public editorItemService: EditorItemsService,
    public loginService: LoginService
  ) { }

  public iconOptions = this.iconList.map(icon => ({
    label: $localize`:@@bot-icon:Icon:`,
    value: icon
  }));

  public get colorOptions() {
    const options = this.colorList.map(color => ({
      label: color === TRANSPARENT_COLOR ? $localize`:@@bot-color-transparent:Transparent` : `${$localize`:@@bot-color:Farbe:`} ${color}`,
      value: color
    }));

    const currentColor = this.rss.botConfig.color;
    if (currentColor && !this.colorList.includes(currentColor)) {
      options.unshift({
        label: `${$localize`:@@bot-color:Farbe:`} ${currentColor}`,
        value: currentColor
      });
    }

    return options;
  }

  protected saveTemperature(): void {
    void this.rss.saveBotConfigField('temperature');
  }

  // Standardmäßig ausgeblendet, bleibt aber sichtbar, sobald bereits ein Bild für den geöffneten Zustand gesetzt ist.
  public get showOpenImageConfig(): boolean {
    return this.openImageConfigRevealed || this.rss.botConfig.hasImageOpen;
  }

  protected revealOpenImageConfig(): void {
    this.openImageConfigRevealed = true;
  }

  protected async saveAutoDeleteTime() {
    if (this.rss.botConfig.secondsToWaitForThreadDeleting < 1) {
      await new Promise(r => setTimeout(r, 25));
      this.rss.botConfig.secondsToWaitForThreadDeleting = 5;
    }
    void this.rss.saveBotConfigField('secondsToWaitForThreadDeleting');
  }

  protected async updateModelSource() {
    await this.rss.saveBotConfigField('modelSource');
    switch (this.rss.botConfig.modelSource) {
      case "openai":
        this.rss.botConfig.model = "gpt-4o-mini";
        break;
      case "vllm":
        this.rss.botConfig.model = this.vllmModels[0];
        break;
    }
    await this.rss.saveBotConfigField('model');
  }

  public get availableModels(): string[] {
    switch (this.rss.botConfig.modelSource) {
      case "openai":
        return [...this.gptModels];
      case "vllm":
        return [...this.vllmModels];
      default:
        return [];
    }
  }

  protected async checkReasoning() {

    let vorhandeneOptionen = reasoningEffortOptions.filter(
      option => option.supported.includes(this.rss.botConfig.model) || option.value === null
    );
    if (!vorhandeneOptionen.some(option => option.value === this.rss.botConfig.reasoningEffort)) {
      try {
        let currentId = reasoningEffortOptions.find(option => option.value === this.rss.botConfig.reasoningEffort)?.id ?? 0;
        this.rss.botConfig.reasoningEffort = vorhandeneOptionen.reduce((nearest, current) =>
          Math.abs(current.id - currentId) < Math.abs(nearest.id - currentId) ? current : nearest).value as ChatbotConfig['reasoningEffort'];
      } catch (e) { this.rss.botConfig.reasoningEffort = null; }
      await this.rss.saveBotConfigField('reasoningEffort');
    }
  }

  protected async checkWebSearch() {
    if (this.rss.botConfig.model === 'gpt-4.1-nano' || this.rss.botConfig.modelSource !== 'openai') {
      if (this.rss.botConfig.websearch) {
        this.rss.botConfig.websearch = false;
        await this.rss.saveBotConfigField('websearch');
      }
    }
  }
}
